import { ContactModal } from '@/components/modals';
import usePreference from '@/hooks/use-preference';
import IconPsychology from '@/icons/icon-psychology';
import IconSentimentDissatisfied from '@/icons/icon-sentiment-dissatisfied';
import IconSentimentNeutral from '@/icons/icon-sentiment-neutral';
import IconSentimentSatisfied from '@/icons/icon-sentiment-satisfied';
import { Property } from '@/types';
import { formatCurrency } from '@/utility';
import clsx from 'clsx';
import {
  SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

enum HighlightLevel {
  Match = 'match',
  Similar = 'similar',
  NotMatch = 'notmatch',
}

interface HighlightChipProps {
  label: string;
  level: HighlightLevel;
}

const HighlightChip: React.FC<HighlightChipProps> = (props) => {
  const Icon: React.FC<SVGProps<SVGSVGElement>> = useMemo(() => {
    return props.level === HighlightLevel.Match
      ? IconSentimentSatisfied
      : props.level === HighlightLevel.Similar
      ? IconSentimentNeutral
      : IconSentimentDissatisfied;
  }, [props.level]);

  const showShadow = true;
  const showBorder = true;

  return (
    <div
      className={clsx(
        'ml-2 mt-2 inline-flex py-1 px-2 rounded flex-row items-center text-sm',
        showShadow && 'shadow',
        props.level === HighlightLevel.Match &&
          'text-primary-500 border-primary-500',
        props.level === HighlightLevel.Similar &&
          'text-yellow-500 border-yellow-500',
        props.level === HighlightLevel.NotMatch &&
          'text-red-500 border-red-500',
        showBorder && 'border'
      )}
    >
      <Icon width={20} height={20} className="-m-1" />
      <span className="ml-2">{props.label}</span>
    </div>
  );
};

interface CustomizeButtonProps {
  onClick?: () => void;
}

const CustomizeButton: React.FC<CustomizeButtonProps> = (props) => {
  const showShadow = true;
  const showBorder = true;

  return (
    <div
      role="button"
      onClick={props.onClick}
      className={clsx(
        'ml-2 mt-2 inline-flex py-1 px-2 rounded flex-row items-center text-sm',
        showShadow && 'shadow',
        'border-primary-500 bg-primary-600 text-white hover:bg-primary-700 hover:text-white',
        showBorder && 'border'
      )}
    >
      <IconPsychology width={20} height={20} className="-m-1" />
      <span className="ml-2">Customize</span>
    </div>
  );
};

interface HightlightChipWrapperProps<
  T extends number | string,
  U extends number | string[]
> {
  value: T;
  prefer: U;
  limit: U;
  higherIsBetter?: boolean;
  label: string;
  pluralLabel?: string;
  emptyLabel?: string;
  hideIfEmpty?: boolean;
  emptyLevel?: HighlightLevel;
  formatter?: (n: number) => string;
}

function HightlightChipWrapper<
  T extends number | string,
  U extends number | string[]
>(props: HightlightChipWrapperProps<T, U>) {
  const prefer = props.prefer || props.limit;
  const limit = props.limit || props.prefer;
  const higherIsBetter = props.higherIsBetter || false;
  const value = props.value;
  const label = props.label;
  const pluralLabel = props.pluralLabel || label;
  const formatterRef = useRef<(n: number) => string>(props.formatter);

  useEffect(() => {
    formatterRef.current = props.formatter;
  }, [props.formatter]);

  const formatter = useCallback((n: number) => {
    if (formatterRef.current) return formatterRef.current(n);
    return n?.toString();
  }, []);

  const isArrayMethod = useMemo(
    () => Array.isArray(prefer) || Array.isArray(limit),
    [prefer, limit]
  );

  const [chipLevel, chipLabel] = useMemo(() => {
    if (!prefer && !limit) return [null, null];

    if (
      isArrayMethod &&
      !(prefer as string[])?.length &&
      !(limit as string[])?.length
    ) {
      return [null, null];
    }

    let cLevel = HighlightLevel.NotMatch;
    let cLabel = label;
    if (!value) {
      if (isArrayMethod) {
        cLevel = HighlightLevel.NotMatch;
        cLabel = `Missing ${label}`;
      } else {
        if (props.hideIfEmpty) {
          cLabel = null;
          cLevel = null;
        } else {
          cLabel = props.emptyLabel || `No ${label}`;
          cLevel =
            props.emptyLevel ||
            (higherIsBetter ? HighlightLevel.NotMatch : HighlightLevel.Match);
        }
      }
    } else {
      if (isArrayMethod) {
        cLabel = value as string;
        if ((prefer as string[]).includes(value as string)) {
          cLevel = HighlightLevel.Match;
        } else if ((limit as string[]).includes(value as string)) {
          cLevel = HighlightLevel.Similar;
        } else {
          cLevel = HighlightLevel.NotMatch;
        }
      } else {
        const v = value as unknown as number;
        cLabel = `${formatter(v)} ${v === 1 ? label : pluralLabel}`;

        if (higherIsBetter) {
          if (v >= prefer) {
            cLevel = HighlightLevel.Match;
          } else if (v >= limit) {
            cLevel = HighlightLevel.Similar;
          } else {
            cLevel = HighlightLevel.NotMatch;
          }
        } else {
          if (v <= prefer) {
            cLevel = HighlightLevel.Match;
          } else if (v <= limit) {
            cLevel = HighlightLevel.Similar;
          } else {
            cLevel = HighlightLevel.NotMatch;
          }
        }
      }
    }
    return [cLevel, cLabel];
  }, [
    prefer,
    limit,
    value,
    higherIsBetter,
    label,
    pluralLabel,
    formatter,
    props.emptyLabel,
    isArrayMethod,
    props.hideIfEmpty,
    props.emptyLevel,
  ]);

  if (isArrayMethod && !chipLabel) {
    // Patch
    return (
      <HighlightChip label={value as string} level={HighlightLevel.Match} />
    );
  }

  if (chipLevel == null) return null;

  return <HighlightChip label={chipLabel} level={chipLevel} />;
}

interface HightlightPreferenceSectionProps {
  property: Property;
}

const HightlightPreferenceSection: React.FC<
  HightlightPreferenceSectionProps
> = (props) => {
  const preference = usePreference(props.property);
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-row flex-wrap -ml-2">
      <HightlightChipWrapper
        value={props.property.propertyType}
        prefer={preference.propertyType?.prefer}
        limit={preference.propertyType?.limit}
        label="type"
      />
      <HightlightChipWrapper
        value={props.property.listPrice}
        prefer={preference.budget?.prefer}
        limit={preference.budget?.limit}
        higherIsBetter={preference.budget?.higherIsBetter}
        label=""
        emptyLabel="Price is missing"
        formatter={(n: number) => formatCurrency(n)}
      />
      <HightlightChipWrapper
        value={(props.property.beds || 0) + (props.property.bedsPlus || 0)}
        prefer={preference.bedroom?.prefer}
        limit={preference.bedroom?.limit}
        higherIsBetter={preference.bedroom?.higherIsBetter}
        label="bedroom"
        pluralLabel="bedrooms"
        emptyLabel="No bedroom"
      />
      <HightlightChipWrapper
        value={(props.property.bath || 0) + (props.property.bathsPlus || 0)}
        prefer={preference.bathroom?.prefer}
        limit={preference.bathroom?.limit}
        higherIsBetter={preference.bathroom?.higherIsBetter}
        label="bathroom"
        pluralLabel="bathrooms"
        emptyLabel="No bathroom"
      />
      <HightlightChipWrapper
        value={props.property.parking}
        prefer={preference.parking?.prefer}
        limit={preference.parking?.limit}
        higherIsBetter={preference.parking?.higherIsBetter}
        label="parking"
        pluralLabel="parkings"
        emptyLabel="Parking info not found"
      />
      <HightlightChipWrapper
        value={props.property.maintanceFee}
        prefer={preference.maintenanceFee?.prefer}
        limit={preference.maintenanceFee?.limit}
        higherIsBetter={preference.maintenanceFee?.higherIsBetter}
        label="maintenance fee"
        emptyLabel="Maintenance fee info not found"
        emptyLevel={HighlightLevel.Similar}
        hideIfEmpty
        formatter={(n: number) => formatCurrency(n)}
      />
      <HightlightChipWrapper
        value={props.property.annualTaxes}
        prefer={preference.annualTax?.prefer}
        limit={preference.annualTax?.limit}
        higherIsBetter={preference.annualTax?.higherIsBetter}
        label="annual tax"
        emptyLabel="Annual tax info not found"
        emptyLevel={HighlightLevel.Similar}
        formatter={(n: number) => formatCurrency(n)}
      />
      <CustomizeButton onClick={() => setOpen(true)} />
      <ContactModal
        open={open}
        toggleModal={() => setOpen(false)}
        message={
          'I would like to customize my preferences on property detail page.'
        }
        booking
      />
    </div>
  );
};

export default HightlightPreferenceSection;
